import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReviewTabs from './ReviewTabs';

function Money({ v }) { return <>${Number(v || 0).toFixed(4)}</>; }
const money = v => `$${Number(v || 0).toFixed(4)}`;

function BreakdownTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;
  const max = Math.max(...rows.map(r => r.cost_usd), 0.0001);
  return (
    <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 14 }}>{title}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.sort((a, b) => b.cost_usd - a.cost_usd).map(r => (
          <div key={r.key} style={{ fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{r.key}</span>
              <span style={{ color: 'var(--text-muted)' }}>
                {(r.input_tokens + r.output_tokens).toLocaleString()} tok · {r.prs} PRs · <b style={{ color: 'var(--text-primary)' }}><Money v={r.cost_usd} /></b>
              </span>
            </div>
            <div style={{ height: 5, background: 'var(--bg-elevated)', borderRadius: 999, marginTop: 3 }}>
              <div style={{ width: `${(r.cost_usd / max) * 100}%`, height: '100%', background: 'var(--accent)', borderRadius: 999 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingEditor() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState({});
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    axios.get('/code-review/pricing').then(res => setRows(res.data.data || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save(m) {
    const e = editing[m];
    try {
      await axios.put(`/code-review/pricing/${encodeURIComponent(m)}`, {
        input_per_mtok: Number(e.input_per_mtok),
        output_per_mtok: Number(e.output_per_mtok),
        cache_read_per_mtok: Number(e.cache_read_per_mtok || 0),
      });
      setMsg(`Saved pricing for ${m}`);
      setEditing(prev => { const n = { ...prev }; delete n[m]; return n; });
      load();
    } catch { setMsg('Failed to save pricing'); }
  }

  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <h3 style={{ marginTop: 0, fontSize: 14 }}>Model pricing (USD per 1M tokens) — edits re-price history retroactively</h3>
      {msg && <div className="alert alert-success" style={{ marginBottom: 10 }}>{msg}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 12 }}>
          <th style={{ padding: 6 }}>Model</th><th style={{ padding: 6 }}>Input</th><th style={{ padding: 6 }}>Output</th><th style={{ padding: 6 }}>Cache read</th><th></th>
        </tr></thead>
        <tbody>
          {rows.map(r => {
            const e = editing[r.model] || r;
            const dirty = !!editing[r.model];
            const upd = (k, v) => setEditing(prev => ({ ...prev, [r.model]: { ...(prev[r.model] || r), [k]: v } }));
            return (
              <tr key={r.model} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: 6 }}><code>{r.model}</code></td>
                {['input_per_mtok', 'output_per_mtok', 'cache_read_per_mtok'].map(k => (
                  <td key={k} style={{ padding: 6 }}>
                    <input type="number" step="0.0001" className="form-control" style={{ width: 90 }} value={e[k] ?? 0} onChange={ev => upd(k, ev.target.value)} />
                  </td>
                ))}
                <td style={{ padding: 6 }}>{dirty && <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => save(r.model)}>Save</button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DailyBudget() {
  const [data, setData] = useState(null);
  const [cap, setCap] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get('/code-review/settings').then(res => {
      setData(res.data);
      setCap(res.data.daily_cost_cap_usd != null ? String(res.data.daily_cost_cap_usd) : '');
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true); setMsg('');
    try {
      await axios.put('/code-review/settings', { daily_cost_cap_usd: cap === '' ? 0 : Number(cap) });
      setMsg(cap === '' ? 'Daily cap cleared (unlimited).' : `Daily cap set to ${money(cap)}.`);
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Failed to save cap'); }
    finally { setSaving(false); }
  }

  if (!data) return null;
  const capSet = data.daily_cost_cap_usd != null;
  const pct = capSet ? Math.min(100, (data.today_spend_usd / data.daily_cost_cap_usd) * 100) : 0;
  return (
    <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 14 }}>Daily cost cap</h3>
      <p className="form-hint" style={{ marginTop: -4 }}>
        A global ceiling across all configs and repos. When today's total review spend reaches it, running scans
        stop before the next PR and new reviews are blocked until tomorrow. Leave blank for no limit.
      </p>
      {msg && <div className="alert alert-success" style={{ marginBottom: 10 }}>{msg}</div>}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="form-label">Cap (USD / day)</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ color: 'var(--text-muted)' }}>$</span>
            <input type="number" min="0" step="0.01" className="form-control" style={{ width: 120 }}
              placeholder="unlimited" value={cap} onChange={e => setCap(e.target.value)} />
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 12, color: data.over_cap ? 'var(--danger)' : 'var(--text-muted)', marginBottom: 4 }}>
            Spent today: <b style={{ color: 'var(--text-primary)' }}><Money v={data.today_spend_usd} /></b>
            {capSet && <> of <Money v={data.daily_cost_cap_usd} />
              {' '}· {data.over_cap ? 'cap reached' : <><Money v={data.today_remaining_usd} /> remaining</>}</>}
          </div>
          {capSet && (
            <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 999 }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: data.over_cap ? 'var(--danger)' : 'var(--accent)' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReviewCosts() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`/code-review/costs?days=${days}`)
      .then(res => setData(res.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load costs'));
  }, [days]);

  return (
    <div>
      <div className="page-header">
        <h2>Cost & Usage</h2>
        <p>Token spend across reviews — priced at current rates.</p>
      </div>
      <div className="page-body">
        <ReviewTabs />
        <div className="toolbar">
          <label style={{ fontSize: 13, color: 'var(--text-muted)' }}>Window:&nbsp;
            <select className="form-control" style={{ width: 130, display: 'inline-block' }} value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </label>
          <div className="spacer" />
        </div>

        {error && <div className="error-msg">{error}</div>}
        <DailyBudget />
        {!data ? <div className="spinner" /> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Total cost', value: <Money v={data.totals.cost_usd} /> },
                { label: 'Input tokens', value: data.totals.input_tokens.toLocaleString() },
                { label: 'Output tokens', value: data.totals.output_tokens.toLocaleString() },
                { label: 'PRs reviewed', value: data.totals.prs },
                { label: 'Findings', value: data.totals.findings },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.label}</div>
                  <div style={{ fontWeight: 700, fontSize: 20 }}>{s.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <BreakdownTable title="By repository" rows={data.by_repo} />
              <BreakdownTable title="By config" rows={data.by_config} />
              <BreakdownTable title="By model" rows={data.by_model} />
              <BreakdownTable title="By day" rows={data.by_day} />
            </div>

            <PricingEditor />
          </>
        )}
      </div>
    </div>
  );
}
